import { forwardRef } from 'react';
import PhoneInput from 'react-phone-number-input';
import labelsEn from 'react-phone-number-input/locale/en.json';
import labelsFr from 'react-phone-number-input/locale/fr.json';
import { useTranslation } from 'react-i18next';
import 'react-phone-number-input/style.css';

import { Input } from '../ui/input';
import './phoneInput.css';

/**
 * <PhoneInput> — shared international phone input.
 *
 * Wraps `react-phone-number-input` with our Shadcn Input styling and
 * i18next-driven country labels. Stores E.164 format (e.g. `+33612345678`)
 * so the backend can validate cleanly and the same string works across
 * SMS gateways / CRM exports / etc.
 *
 * Props (all forwarded to PhoneInput; see the upstream lib for the
 * full list — https://catamphetamine.gitlab.io/react-phone-number-input):
 *   value            – E.164 string (or legacy free-text; the input is
 *                      tolerant so existing customer data won't crash)
 *   onChange         – (e164OrEmpty) => void
 *   defaultCountry   – ISO code used when the value is empty (default "FR")
 *   disabled         – bool
 *   testid           – applied to the number <input>; the country
 *                      selector gets `${testid}-country-select`
 *   inputClassName   – extra Tailwind classes on the number input
 */
const StyledInput = forwardRef(({ className = '', ...rest }, ref) => (
    <Input
        ref={ref}
        {...rest}
        className={`focus:border-[#C08478] focus:ring-[#C08478]/20 ${className}`}
    />
));
StyledInput.displayName = 'SharedPhoneInputField';

export const AppPhoneInput = ({
    value,
    onChange,
    defaultCountry = 'FR',
    disabled,
    testid = 'phone-input',
    inputClassName = '',
    ...rest
}) => {
    const { i18n } = useTranslation();
    const labels = (i18n.resolvedLanguage || 'en').startsWith('fr') ? labelsFr : labelsEn;

    return (
        <div className="portal-phone-wrapper" data-testid={`${testid}-wrapper`}>
            <PhoneInput
                international
                defaultCountry={defaultCountry}
                value={value || ''}
                onChange={(v) => onChange(v || '')}
                labels={labels}
                disabled={disabled}
                inputComponent={StyledInput}
                numberInputProps={{
                    'data-testid': testid,
                    className: inputClassName,
                }}
                countrySelectProps={{ 'data-testid': `${testid}-country-select` }}
                {...rest}
            />
        </div>
    );
};

export default AppPhoneInput;
